"use client";
import React, { useState } from "react";
import { Route, Lock, Unlock, CheckCircle2, AlertCircle } from "lucide-react";

export const InteractiveEndpoint = () => {
    const [method, setMethod] = useState<"GET" | "POST" | "PUT" | "DELETE">("GET");
    const [path, setPath] = useState("/api/users/:id");
    const [requiresAuth, setRequiresAuth] = useState(true);
    const [selectedFunction, setSelectedFunction] = useState("getUserById");

    const methods = ["GET", "POST", "PUT", "DELETE"];
    const functions = ["getUserById", "createUser", "updateUser", "deleteUser"];

    const methodColors: Record<string, string> = {
        GET: "bg-blue-500/10 text-blue-500 border-blue-500/30",
        POST: "bg-green-500/10 text-green-500 border-green-500/30",
        PUT: "bg-amber-500/10 text-amber-500 border-amber-500/30",
        DELETE: "bg-red-500/10 text-red-500 border-red-500/30"
    };

    return (
        <div className="w-full max-w-2xl mx-auto bg-surface border border-border rounded-xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-background/50 backdrop-blur-sm p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-primary/10 rounded-lg">
                        <Route className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-semibold text-primary-text text-sm">Endpoint Builder</span>
                </div>
                <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/20 border border-red-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/20 border border-yellow-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/20 border border-green-500/50" />
                </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6 bg-gradient-to-b from-surface to-background">
                {/* HTTP Method Selection */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                        HTTP Method
                    </label>
                    <div className="flex gap-2">
                        {methods.map((m) => (
                            <button
                                key={m}
                                onClick={() => setMethod(m as any)}
                                className={`px-4 py-2 rounded-lg text-sm font-bold border transition-all ${method === m
                                        ? methodColors[m]
                                        : "bg-background border-border text-secondary-text hover:border-primary/30"
                                    }`}
                            >
                                {m}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Path Input */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                        Endpoint Path
                    </label>
                    <input
                        type="text"
                        value={path}
                        onChange={(e) => setPath(e.target.value)}
                        className="w-full bg-background border border-border rounded-lg p-3 text-sm text-primary-text font-mono focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                        placeholder="/api/resource/:id"
                    />
                </div>

                {/* Authentication Toggle */}
                <div className="flex items-center justify-between p-4 bg-background/50 rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                        {requiresAuth ? (
                            <Lock className="w-5 h-5 text-amber-500" />
                        ) : (
                            <Unlock className="w-5 h-5 text-green-500" />
                        )}
                        <div>
                            <div className="text-sm font-semibold text-primary-text">Authentication</div>
                            <div className="text-xs text-secondary-text">
                                {requiresAuth ? "Protected endpoint" : "Public access"}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => setRequiresAuth(!requiresAuth)}
                        className={`w-11 h-6 rounded-full relative transition-colors ${requiresAuth ? "bg-primary" : "bg-border"
                            }`}
                    >
                        <div
                            className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${requiresAuth ? "left-6" : "left-1"
                                }`}
                        />
                    </button>
                </div>

                {/* Function Selection */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                        Handler Function
                    </label>
                    <div className="space-y-2">
                        {functions.map((fn) => (
                            <button
                                key={fn}
                                onClick={() => setSelectedFunction(fn)}
                                className={`w-full text-left p-3 rounded-lg border transition-all ${selectedFunction === fn
                                        ? "bg-primary/10 border-primary text-primary-text"
                                        : "bg-background border-border text-secondary-text hover:border-primary/30"
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="font-mono text-sm">{fn}</span>
                                    {selectedFunction === fn && (
                                        <CheckCircle2 className="w-4 h-4 text-primary" />
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Preview */}
                <div className="p-4 bg-background/80 rounded-lg border border-border/50">
                    <div className="text-xs font-medium text-secondary-text uppercase tracking-wider mb-2">
                        Preview
                    </div>
                    <div className="font-mono text-sm space-y-1">
                        <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${methodColors[method]}`}>
                                {method}
                            </span>
                            <span className="text-primary-text">{path}</span>
                        </div>
                        <div className="text-xs text-secondary-text flex items-center gap-2">
                            {requiresAuth ? (
                                <>
                                    <Lock className="w-3 h-3 text-amber-500" />
                                    <span>Requires authentication</span>
                                </>
                            ) : (
                                <>
                                    <Unlock className="w-3 h-3 text-green-500" />
                                    <span>Public access</span>
                                </>
                            )}
                        </div>
                        <div className="text-xs text-secondary-text">
                            Handler: <span className="text-primary">{selectedFunction}()</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
